import * as React from 'react';
import { Icon, Link } from '@fluentui/react';
import styles from './SharePointOrganizer.module.scss';
import type { IScanResult } from '../../../models/ScanResult';
import type { IFolderItem } from '../../../models/FolderItem';
import type { IFileItem } from '../../../models/FileItem';
import { formatBytes, formatNumber } from '../../../utils/format';

/** Files listed per folder before a "Show all" link, to keep huge folders responsive. */
const FILE_PREVIEW_LIMIT: number = 50;
const INDENT_PX: number = 20;

export interface IFolderTreeProps {
  result: IScanResult;
}

interface IFolderNodeProps {
  folder: IFolderItem;
  result: IScanResult;
  expanded: { [id: string]: boolean };
  showAll: { [id: string]: boolean };
  onToggle: (id: string) => void;
  onShowAll: (id: string) => void;
}

function FileRow(props: { file: IFileItem; depth: number }): React.ReactElement {
  const { file, depth } = props;
  return (
    <li className={styles.treeRow} style={{ paddingLeft: depth * INDENT_PX + INDENT_PX }}>
      <Icon iconName="Page" className={styles.treeIcon} />
      <Link href={file.webUrl} target="_blank" rel="noreferrer" className={styles.treeName}>{file.name}</Link>
      <span className={styles.treeMeta}>{formatBytes(file.size)}</span>
    </li>
  );
}

function FolderNode(props: IFolderNodeProps): React.ReactElement {
  const { folder, result, expanded, showAll, onToggle, onShowAll } = props;
  const isOpen: boolean = !!expanded[folder.id];
  const hasChildren: boolean = folder.childFolderIds.length > 0 || folder.fileIds.length > 0;
  const visibleFileIds: string[] = showAll[folder.id] ? folder.fileIds : folder.fileIds.slice(0, FILE_PREVIEW_LIMIT);
  const hiddenFiles: number = folder.fileIds.length - visibleFileIds.length;

  return (
    <>
      <li className={styles.treeRow} style={{ paddingLeft: folder.depth * INDENT_PX }}>
        <button
          type="button"
          className={styles.treeToggle}
          onClick={() => onToggle(folder.id)}
          aria-expanded={hasChildren ? isOpen : undefined}
          disabled={!hasChildren}
        >
          <Icon iconName={hasChildren ? (isOpen ? 'ChevronDown' : 'ChevronRight') : ''} className={styles.treeChevron} />
          <Icon iconName={isOpen ? 'FabricOpenFolderHorizontal' : 'FabricFolder'} className={styles.treeIcon} />
          <span className={styles.treeName}>{folder.name}</span>
          {result.protectedFolders[folder.id] && (
            <Icon iconName="Lock" className={styles.treeIcon} title={result.protectedFolders[folder.id]} aria-label="Protected" />
          )}
        </button>
        <span className={styles.treeMeta}>
          {formatNumber(folder.totalFileCount)} {folder.totalFileCount === 1 ? 'file' : 'files'} · {formatBytes(folder.totalSize)}
        </span>
      </li>
      {isOpen && folder.childFolderIds.map(id => (
        <FolderNode key={id} {...props} folder={result.folders[id]} />
      ))}
      {isOpen && visibleFileIds.map(id => (
        <FileRow key={id} file={result.files[id]} depth={folder.depth} />
      ))}
      {isOpen && hiddenFiles > 0 && (
        <li className={styles.treeRow} style={{ paddingLeft: folder.depth * INDENT_PX + INDENT_PX }}>
          <Link onClick={() => onShowAll(folder.id)}>Show {formatNumber(hiddenFiles)} more files</Link>
        </li>
      )}
    </>
  );
}

/** Expandable view of the scanned library. Children render only once a folder is opened. */
export default function FolderTree(props: IFolderTreeProps): React.ReactElement {
  const { result } = props;
  const [expanded, setExpanded] = React.useState<{ [id: string]: boolean }>({ [result.rootId]: true });
  const [showAll, setShowAll] = React.useState<{ [id: string]: boolean }>({});

  React.useEffect(() => {
    setExpanded({ [result.rootId]: true });
    setShowAll({});
  }, [result]);

  const onToggle = React.useCallback((id: string): void => {
    setExpanded(prev => ({ ...prev, [id]: !prev[id] }));
  }, []);
  const onShowAll = React.useCallback((id: string): void => {
    setShowAll(prev => ({ ...prev, [id]: true }));
  }, []);

  return (
    <div className={styles.treePanel}>
      <ul className={styles.tree}>
        <FolderNode
          folder={result.folders[result.rootId]}
          result={result}
          expanded={expanded}
          showAll={showAll}
          onToggle={onToggle}
          onShowAll={onShowAll}
        />
      </ul>
    </div>
  );
}
